import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'

import { ThemeDynamicIslandHost } from '../components/theme/dynamic-island'
import { DynamicIslandActivityDetailModal } from '../components/theme/dynamic-island/DynamicIslandActivityDetailModal'
import { useTheme } from '../theme/ThemeContext'
import { DynamicIslandContext } from './DynamicIslandContext'
import type { DynamicIslandActivity } from './dynamicIslandTypes'

function sortActivities(items: DynamicIslandActivity[]): DynamicIslandActivity[] {
  return [...items].sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id))
}

function pickDefaultSelectedId(items: DynamicIslandActivity[]): string | null {
  if (items.length === 0) return null
  const active = items.find((item) => item.lifecycle === 'active')
  return active?.id ?? items[0]!.id
}

export function DynamicIslandProvider({ children }: { children: ReactNode }) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const [expanded, setExpanded] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detailActivityId, setDetailActivityId] = useState<string | null>(null)
  const [activityMap, setActivityMap] = useState<Map<string, DynamicIslandActivity>>(() => new Map())
  const [contentRevision, bumpContent] = useReducer((n: number) => n + 1, 0)
  const bumpRafRef = useRef<number | null>(null)

  const reportActivity = useCallback((activity: DynamicIslandActivity) => {
    setActivityMap((prev) => {
      const next = new Map(prev)
      next.set(activity.id, activity)
      return next
    })
  }, [])

  const clearActivity = useCallback((id: string) => {
    setActivityMap((prev) => {
      if (!prev.has(id)) return prev
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  const notifyContentChange = useCallback(() => {
    if (bumpRafRef.current != null) return
    bumpRafRef.current = requestAnimationFrame(() => {
      bumpRafRef.current = null
      bumpContent()
    })
  }, [])

  const openActivityDetail = useCallback((id: string) => {
    setDetailActivityId(id)
    setSelectedId(id)
  }, [])

  const closeActivityDetail = useCallback(() => {
    setDetailActivityId(null)
  }, [])

  const activities = useMemo(
    () => sortActivities([...activityMap.values()]),
    [activityMap, contentRevision],
  )

  const activityIdsKey = activities.map((item) => item.id).join('\0')

  useEffect(() => {
    if (activities.length === 0) {
      setSelectedId(null)
      setExpanded(false)
      setDetailActivityId(null)
      return
    }
    setSelectedId((prev) => {
      if (prev && activities.some((item) => item.id === prev)) return prev
      return pickDefaultSelectedId(activities)
    })
  }, [activityIdsKey, activities])

  useEffect(() => {
    if (!detailActivityId) return
    if (!activities.some((item) => item.id === detailActivityId)) {
      setDetailActivityId(null)
    }
  }, [activities, detailActivityId])

  const value = useMemo(
    () => ({
      expanded,
      setExpanded,
      selectedId,
      setSelectedId,
      detailActivityId,
      openActivityDetail,
      closeActivityDetail,
      activities,
      reportActivity,
      clearActivity,
      notifyContentChange,
    }),
    [
      expanded,
      selectedId,
      detailActivityId,
      openActivityDetail,
      closeActivityDetail,
      activities,
      reportActivity,
      clearActivity,
      notifyContentChange,
    ],
  )

  const detailSelectedId = detailActivityId ?? selectedId

  return (
    <DynamicIslandContext.Provider value={value}>
      {children}
      <ThemeDynamicIslandHost
        angular={angular}
        activities={activities}
        selectedId={selectedId}
        onSelectId={setSelectedId}
        expanded={expanded}
        setExpanded={setExpanded}
        contentRevision={contentRevision}
        detailModalOpen={detailActivityId != null}
      />
      <DynamicIslandActivityDetailModal
        open={detailActivityId != null}
        angular={angular}
        activities={activities}
        selectedId={detailSelectedId}
        onSelectId={(id) => {
          setDetailActivityId(id)
          setSelectedId(id)
        }}
        onClose={closeActivityDetail}
      />
    </DynamicIslandContext.Provider>
  )
}
